/**
 * F5 · ভাষা ও দেশ — `/account/language`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame F5 (360 × 680).
 *
 * DEVIATES FROM THE FRAME, BY FOUNDER DECISION (2026-09-18). The frame draws a মোড block
 * under the country line — a ব্যক্তিগত row, a locked ফাউন্ডার row leading to the founder
 * entry screen, and a note that re-verification follows. Founder mode now lives on
 * founder.oprappo.com only and the customer's app shows no founder mode anywhere, so the
 * block is gone: the server sends no mode rows and this screen draws none. Recorded in
 * docs/deviations/W1-03.md, where the visual gate reports F5 as DEVIATED rather than as a
 * pass; the design file is not edited. Everything above the block is unchanged.
 *
 * WIRED. `GET /v1/account/profile` carries F5's whole payload — both language rows and
 * the country line — and `PATCH /v1/account/language` writes the choice. The write
 * answers with F5's payload and the screen re-renders from it: a locally-flipped radio the
 * server then refused would show a language the person is not in.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AccountLanguage, LanguageSettings } from '@oprappo/contracts';
import { Frame, Radio, SAND_FRAME, ScreenHeader, SelectChevron } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchAccount, setLanguage } from './api.js';

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function F5LanguageView({ data }: { data: LanguageSettings | null }): JSX.Element {
  const navigate = useNavigate();

  // The screen re-renders from the WRITE's answer, not from a local guess: the route
  // returns F5's whole payload, and a locally-flipped radio that the server then refused
  // would show a language the person is not actually in.
  const [saved, setSaved] = useState<LanguageSettings | null>(null);
  const l = saved ?? data;
  const lang: AccountLanguage | null = l?.selected ?? null;

  const choose = (id: AccountLanguage): void => {
    void setLanguage({ language: id }).then(setSaved);
  };

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader titleBn={l?.title_bn ?? ''} onBack={() => navigate('/account')} />

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mb-[11px]">
          {l?.language_heading_bn ?? ''}
        </div>
        <div className="flex flex-col gap-[9px]">
          {(l?.languages ?? []).map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={lang === option.id}
              onClick={() => choose(option.id)}
              className={
                // Drawn `height:54px;padding:0 15px` with a rule each side. The canvas is
                // content-box outside a frame box, so that is a 56px outer row; written as
                // the outer height because `w-full` and content-box cannot both hold.
                // `py-0`: preflight is off, so a <button> keeps the UA's `padding:1px 6px`.
                'bg-white h-[56px] flex items-center justify-between px-[15px] py-0 w-full rounded-none text-left ' +
                (lang === option.id ? 'border-[1.5px] border-teal' : 'border border-border')
              }
            >
              {/*
                * `latin` comes from the server, because WHICH row is set in Manrope is a
                * fact about the language rather than about its position: `English` is
                * drawn `500 16px/1.2` in Manrope as its own language, বাংলা in Anek at
                * the Bangla band. An index check here would break the moment a third
                * language lands.
                */}
              <span
                className={
                  option.latin
                    ? 'font-manrope font-medium text-[16px] leading-[1.2] text-body'
                    : 'font-anek font-medium text-[16px] leading-[1.75] text-ink'
                }
              >
                {option.label_bn}
              </span>
              <Radio selected={lang === option.id} />
            </button>
          ))}
        </div>

        <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mt-[20px] mb-[11px]">
          {l?.country_heading_bn ?? ''}
        </div>
        <div className="border border-border bg-white h-[54px] box-content flex items-center justify-between px-[15px]">
          <div className="font-anek font-normal text-[16px] leading-[1.75] text-ink">
            {l?.country_bn ?? ''}
          </div>
          <SelectChevron size={17} />
        </div>
      </div>
    </Frame>
  );
}

export function F5Language(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchAccount(signal), []);
  const { data, error } = useResource(load, 'account');

  return (
    <SurfaceStates surface="account" error={error ?? undefined}>
      <F5LanguageView data={data?.language ?? null} />
    </SurfaceStates>
  );
}
